import {
  useState,
  useEffect,
  useCallback,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import { SharedAccess } from "target_wasm";
import { ShareSuccessContext } from "./ShareSection";
import { DirectRole, RefetchOptions } from "./entities";
import { FetchSharedResult } from "@/components/canvas/detailsSide/tableDetails/TableServices";

export type FetchSharedWithListFn = (
  entityId: string,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  search?: string,
  setContinuationToken?: Dispatch<SetStateAction<string | null>>,
  continuationToken?: string | null,
  limit?: number,
  role?: DirectRole,
) => Promise<FetchSharedResult | null>;

interface UseSharedAccessManagerOptions {
  entityId: string;
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>;
  fetchFn: FetchSharedWithListFn;
  onPermissionError?: () => Promise<void>;
  onAccessChanged?: () => void;
}

// The server's default page is 6, so Show all would page almost at once.
const PAGE = 20;
// A filtered page can come back empty while more remain, since the server reads
// a bounded number of grants per request; past this many, Load more takes over.
const READ_ON = 3;

/// The first page the list shows, which a refill or a share asks for again.
interface Showing {
  search?: string;
  limit?: number;
  role?: DirectRole;
}

export function useSharedAccessManager(options: UseSharedAccessManagerOptions) {
  const {
    entityId,
    setSharedWithList,
    fetchFn,
    onPermissionError,
    onAccessChanged,
  } = options;

  const [isSuccess, setIsSuccess] = useState(false);
  const [isRevokeAction, setIsRevokeAction] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userContinuationToken, setUserContinuationToken] = useState<
    string | null
  >(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Only the newest request writes the list, so a slow search or page cannot
  // land over a newer one.
  const latestRequest = useRef(0);
  const showing = useRef<Showing>({});

  const request = useCallback(
    async (
      search?: string,
      continuationToken?: string | null,
      limit?: number,
      role?: DirectRole,
    ) => {
      const mine = ++latestRequest.current;
      const unlessStale =
        <T>(set: Dispatch<SetStateAction<T>>): Dispatch<SetStateAction<T>> =>
        (value) => {
          if (latestRequest.current === mine) set(value);
        };
      const page = (token?: string | null) =>
        fetchFn(
          entityId,
          unlessStale(setSharedWithList),
          search,
          unlessStale(setUserContinuationToken),
          token,
          limit ?? PAGE,
          role,
        );
      let result = await page(continuationToken);
      if (result && !continuationToken && latestRequest.current === mine) {
        showing.current = { search, limit, role };
      }
      for (
        let more = 0;
        more < READ_ON &&
        latestRequest.current === mine &&
        result?.continuationToken &&
        result.sharedAccess.users().length === 0;
        more++
      ) {
        // A failed read-on keeps the page it has, and its token, for Load more.
        const next = await page(result.continuationToken);
        if (!next) break;
        result = next;
      }
      return result;
    },
    [entityId, setSharedWithList, fetchFn],
  );

  // The fetch answers null when it fails, which would otherwise leave the
  // dialog on its loading state for good.
  const loadFirstPage = useCallback(() => {
    setLoadFailed(false);
    request(undefined, null).then(
      (result) => setLoadFailed(result === null),
      () => setLoadFailed(true),
    );
  }, [request]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  /// Whether the page arrived and is still the newest; a failed one leaves the
  /// list and its token as they were.
  const handleRefetchSharedList = useCallback(
    async (refetchOpts?: RefetchOptions) => {
      const mine = latestRequest.current + 1;
      const result = await request(
        refetchOpts?.search,
        refetchOpts?.continuationToken,
        refetchOpts?.limit,
        refetchOpts?.role,
      );
      return result !== null && latestRequest.current === mine;
    },
    [request],
  );

  const handleSuccess = useCallback(
    (context: ShareSuccessContext) => {
      setIsRevokeAction(context.isRevoke);
      setIsSuccess(true);
      setErrorMessage(null);

      // A token is bound to its role and search, so the refill carries both.
      const { search, limit, role } = showing.current;
      if (context.isRevoke) {
        if (userContinuationToken) {
          request(search, userContinuationToken, 1, role);
        }
      } else {
        request(search, undefined, limit, role);
      }

      onAccessChanged?.();
      setTimeout(() => setIsSuccess(false), 2000);
    },
    [userContinuationToken, request, onAccessChanged],
  );

  const handleError = useCallback(
    async (error: string, status?: number) => {
      setIsSuccess(false);
      setIsRevokeAction(false);
      setErrorMessage(error);
      setTimeout(() => setErrorMessage(null), 2000);

      if ((status === 401 || status === 403) && onPermissionError) {
        await onPermissionError();
      }
    },
    [onPermissionError],
  );

  return {
    isSuccess,
    isRevokeAction,
    errorMessage,
    userContinuationToken,
    loadFailed,
    retryLoad: loadFirstPage,
    handleRefetchSharedList,
    handleSuccess,
    handleError,
  };
}
